import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isFeeApplicable = (fechaInicio: string | null | undefined) => {
    if (!fechaInicio) return false;
    try {
        const d = new Date(fechaInicio);
        return d.getFullYear() > 2026 || (d.getFullYear() === 2026 && d.getMonth() >= 5);
    } catch { return false; }
};

const AdminRecruiterRosterPage: React.FC = () => {
    const { recruiterClave, torneoClave } = useParams<{ recruiterClave: string; torneoClave: string }>();
    const navigate = useNavigate();

    const [bowlers, setBowlers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [recruiterName, setRecruiterName] = useState('');
    const [tournamentName, setTournamentName] = useState('');
    const [tournamentFechaInicio, setTournamentFechaInicio] = useState<string | null>(null);

    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) { navigate('/admin'); return; }
        if (!recruiterClave || !torneoClave) return;

        setLoading(true);
        Promise.all([
            fetch(`${BACKEND_URL}/api/recruiter/roster/${recruiterClave}/${torneoClave}`).then(r => r.json()),
            fetch(`${BACKEND_URL}/api/recruiter/roster/${recruiterClave}`).then(r => r.json()),
            fetch(`${BACKEND_URL}/api/admin/recruiters-for-tournament/${torneoClave}`).then(r => r.json()),
        ])
            .then(([bowlerData, tournamentsData, recruitersData]) => {
                // Tournament name from roster summary
                const tournament = (tournamentsData || []).find((t: any) => String(t.torneoClave) === String(torneoClave));
                if (tournament) {
                    setTournamentName(tournament.torneoNombre);
                    setTournamentFechaInicio(tournament.fechaInicio ?? null);
                }

                // All bowlers; filter to owed only if fee applies
                const feeApplies = tournament ? isFeeApplicable(tournament.fechaInicio) : false;
                setBowlers(feeApplies
                    ? (bowlerData || []).filter((b: any) => !b.webfee || b.webfee !== 1)
                    : (bowlerData || []));

                // Recruiter name
                const rec = (recruitersData || []).find((r: any) => String(r.CVEJUGADOR) === String(recruiterClave));
                if (rec) setRecruiterName(rec.NOMBRECOMPLETO);
            })
            .catch(() => setError('Failed to load roster'))
            .finally(() => setLoading(false));
    }, [recruiterClave, torneoClave, navigate]);

    const filtered = bowlers.filter(b => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            b.nombreCompleto?.toLowerCase().includes(s) ||
            String(b.fichanal).includes(s) ||
            b.email?.toLowerCase().includes(s)
        );
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-lg">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-gray-800 transition p-2 rounded-lg hover:bg-gray-100"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Owed Roster</h1>
                            {(recruiterName || tournamentName) && (
                                <p className="text-sm text-gray-500 leading-tight">
                                    {recruiterName}{tournamentName ? ` — ${tournamentName}` : ''}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                {/* Search + count */}
                <div className="bg-white rounded-2xl shadow-md p-4 mb-5">
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name, TAT ID, or email…"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {!loading && (
                        <p className="text-xs text-gray-400 mt-2">
                            {isFeeApplicable(tournamentFechaInicio)
                                ? `${filtered.length} bowler${filtered.length !== 1 ? 's' : ''} with outstanding balance`
                                : `${filtered.length} bowler${filtered.length !== 1 ? 's' : ''} · Fee tracking not applicable for this tournament`
                            }
                        </p>
                    )}
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
                )}

                {loading ? (
                    <div className="flex justify-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                        {search ? 'No bowlers match your search.' : isFeeApplicable(tournamentFechaInicio) ? 'No bowlers with outstanding balance.' : 'No bowlers in this roster.'}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((bowler: any) => (
                            <div key={bowler.clave} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-gray-900 text-sm">TAT #{bowler.fichanal}</span>
                                            <span className="text-gray-800 text-sm font-medium">{bowler.nombreCompleto}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">{bowler.email || 'No email'}</div>
                                    </div>
                                    {isFeeApplicable(tournamentFechaInicio) && (
                                        <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                                            Owes
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminRecruiterRosterPage;
